import React, { useEffect, useRef, useState } from 'react'
import { Box } from '@mui/material'

import { colors } from '~tokens/internal'

import Button from '../Button'
import ColorInput from '../ColorInput'
import Dropdown from '../Dropdown'
import Icon from '../Icon'
import TextField from '../TextField'

import './ColorPicker.scss'

export interface ColorPickerProps {
  value?: string
  onChange?: (color: string) => void
  className?: string
}

type ColorFormat = 'HEX' | 'RGB'

const PRESET_COLORS = [
  colors.neutral[100],
  colors.red[100],
  colors.orange[100],
  colors.amber[100],
  colors.lime[100],
  colors.green[100],
  colors.teal[100],
  colors.blue[100],
  colors.indigo[100],
  colors.purple[100],
  colors.pink[100],
  colors.neutral[500],
  colors.red[500],
  colors.orange[500],
  colors.amber[500],
  colors.lime[500],
  colors.green[500],
  colors.teal[500],
  colors.blue[500],
  colors.indigo[500],
  colors.purple[500],
  colors.pink[500],
]

const hexToHsv = (hex: string): { h: number; s: number; v: number } => {
  const cleanHex = hex.replace('#', '').padEnd(6, '0')
  const r = parseInt(cleanHex.substring(0, 2), 16) / 255
  const g = parseInt(cleanHex.substring(2, 4), 16) / 255
  const b = parseInt(cleanHex.substring(4, 6), 16) / 255

  const max = Math.max(r, g, b)
  const min = Math.min(r, g, b)
  const d = max - min
  let h = 0
  const s = max === 0 ? 0 : d / max
  const v = max

  if (d !== 0) {
    switch (max) {
      case r:
        h = ((g - b) / d + (g < b ? 6 : 0)) / 6
        break
      case g:
        h = ((b - r) / d + 2) / 6
        break
      case b:
        h = ((r - g) / d + 4) / 6
        break
    }
  }

  return {
    h: Math.round(h * 360),
    s: Math.round(s * 100),
    v: Math.round(v * 100),
  }
}

const hsvToHex = (h: number, s: number, v: number): string => {
  const sNorm = s / 100
  const vNorm = v / 100
  const c = vNorm * sNorm
  const x = c * (1 - Math.abs(((h / 60) % 2) - 1))
  const m = vNorm - c

  let r = 0
  let g = 0
  let b = 0

  if (h >= 0 && h < 60) {
    r = c
    g = x
    b = 0
  } else if (h >= 60 && h < 120) {
    r = x
    g = c
    b = 0
  } else if (h >= 120 && h < 180) {
    r = 0
    g = c
    b = x
  } else if (h >= 180 && h < 240) {
    r = 0
    g = x
    b = c
  } else if (h >= 240 && h < 300) {
    r = x
    g = 0
    b = c
  } else if (h >= 300 && h < 360) {
    r = c
    g = 0
    b = x
  }

  const toHex = (n: number): string =>
    Math.round((n + m) * 255)
      .toString(16)
      .padStart(2, '0')

  return `#${toHex(r)}${toHex(g)}${toHex(b)}`.toUpperCase()
}

const hexToRgb = (hex: string): { r: number; g: number; b: number } => {
  const cleanHex = hex.replace('#', '').padEnd(6, '0')
  return {
    r: parseInt(cleanHex.substring(0, 2), 16),
    g: parseInt(cleanHex.substring(2, 4), 16),
    b: parseInt(cleanHex.substring(4, 6), 16),
  }
}

const rgbToHex = (r: number, g: number, b: number): string => {
  const toHex = (n: number): string => Math.round(n).toString(16).padStart(2, '0')
  return `#${toHex(r)}${toHex(g)}${toHex(b)}`.toUpperCase()
}

const ColorPicker: React.FC<ColorPickerProps> = ({ value = '#000000', onChange, className }) => {
  const [currentColor, setCurrentColor] = useState(value.toUpperCase())
  const [showCustomPicker, setShowCustomPicker] = useState(false)
  const [format, setFormat] = useState<ColorFormat>('HEX')

  const hsv = hexToHsv(currentColor)
  const [hue, setHue] = useState(hsv.h)
  const [saturation, setSaturation] = useState(hsv.s)
  const [brightness, setBrightness] = useState(hsv.v)

  const gradientRef = useRef<HTMLDivElement>(null)
  const hueSliderRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const newColor = value.toUpperCase()
    if (newColor !== currentColor) {
      setCurrentColor(newColor)
      const hsv = hexToHsv(newColor)
      setHue(hsv.h)
      setSaturation(hsv.s)
      setBrightness(hsv.v)
    }
  }, [value])

  const classNames = ['internal-colorpicker', className].filter(Boolean).join(' ')

  const getFormattedColor = (): string => {
    if (format === 'RGB') {
      const rgb = hexToRgb(currentColor)
      return `${rgb.r}, ${rgb.g}, ${rgb.b}`
    }
    return currentColor.replace('#', '')
  }

  const handleBackToPresets = (): void => {
    setShowCustomPicker(false)
  }

  const handleRgbChange = (channel: 'r' | 'g' | 'b', inputValue: number): void => {
    const clampedValue = Math.max(0, Math.min(255, inputValue))

    const rgb = hexToRgb(currentColor)
    const newRgb = {
      r: channel === 'r' ? clampedValue : rgb.r,
      g: channel === 'g' ? clampedValue : rgb.g,
      b: channel === 'b' ? clampedValue : rgb.b,
    }
    const newColor = rgbToHex(newRgb.r, newRgb.g, newRgb.b)
    setCurrentColor(newColor)
    onChange?.(newColor)
    const hsv = hexToHsv(newColor)
    setHue(hsv.h)
    setSaturation(hsv.s)
    setBrightness(hsv.v)
  }

  const handleGradientClick = (e: React.MouseEvent<HTMLDivElement>): void => {
    if (!gradientRef.current) return

    const rect = gradientRef.current.getBoundingClientRect()
    const x = e.clientX - rect.left
    const y = e.clientY - rect.top

    const newSaturation = Math.round((x / rect.width) * 100)
    const newBrightness = Math.round(100 - (y / rect.height) * 100)

    setSaturation(newSaturation)
    setBrightness(newBrightness)

    const newColor = hsvToHex(hue, newSaturation, newBrightness)
    setCurrentColor(newColor)
    onChange?.(newColor)
  }

  const handleHueSliderClick = (e: React.MouseEvent<HTMLDivElement>): void => {
    if (!hueSliderRef.current) return

    const rect = hueSliderRef.current.getBoundingClientRect()
    const x = e.clientX - rect.left
    const newHue = Math.round((x / rect.width) * 360)

    setHue(newHue)

    const newColor = hsvToHex(newHue, saturation, brightness)
    setCurrentColor(newColor)
    onChange?.(newColor)
  }

  const handleColorInput = (e: React.ChangeEvent<HTMLInputElement>): void => {
    const inputValue = e.target.value

    if (format === 'HEX') {
      const hexValue = inputValue.toUpperCase()
      const hexRegex = /^[0-9A-F]{0,6}$/

      if (hexRegex.test(hexValue) && hexValue.length <= 6) {
        if (hexValue.length === 6) {
          const newColor = `#${hexValue}`
          setCurrentColor(newColor)
          onChange?.(newColor)
          const hsv = hexToHsv(newColor)
          setHue(hsv.h)
          setSaturation(hsv.s)
          setBrightness(hsv.v)
        }
      }
    } else if (format === 'RGB') {
      const parts = inputValue.split(',').map((p) => p.trim())
      if (parts.length === 3) {
        const r = parseInt(parts[0]) || 0
        const g = parseInt(parts[1]) || 0
        const b = parseInt(parts[2]) || 0

        if (r >= 0 && r <= 255 && g >= 0 && g <= 255 && b >= 0 && b <= 255) {
          const newColor = rgbToHex(r, g, b)
          setCurrentColor(newColor)
          onChange?.(newColor)
          const hsv = hexToHsv(newColor)
          setHue(hsv.h)
          setSaturation(hsv.s)
          setBrightness(hsv.v)
        }
      }
    }
  }

  const handlePresetClick = (color: string): void => {
    const upperColor = color.toUpperCase()
    setCurrentColor(upperColor)
    onChange?.(upperColor)
    const hsv = hexToHsv(upperColor)
    setHue(hsv.h)
    setSaturation(hsv.s)
    setBrightness(hsv.v)
  }

  const gradientSelectorPosition = {
    left: `${saturation}%`,
    top: `${100 - brightness}%`,
  }

  const hueSliderPosition = {
    left: `${(hue / 360) * 100}%`,
  }

  return (
    <Box className={classNames}>
      {!showCustomPicker ? (
        <Box key='presets' className='internal-colorpicker__view'>
          <Box className='internal-colorpicker__presets'>
            {PRESET_COLORS.map((color) => (
              <Box
                key={color}
                className='internal-colorpicker__preset-swatch'
                style={{ backgroundColor: color }}
                onClick={() => handlePresetClick(color)}
              />
            ))}
          </Box>

          <Button
            variant='text'
            size='small'
            onClick={() => setShowCustomPicker(true)}
            className='internal-colorpicker__custom-color-button'
            endIcon={<Icon name='chevronRight' />}
          >
            Choose custom color
          </Button>
        </Box>
      ) : (
        <Box key='gradient' className='internal-colorpicker__view internal-colorpicker__popover'>
          <Button
            variant='text'
            size='small'
            onClick={handleBackToPresets}
            className='internal-colorpicker__back-button'
            startIcon={<Icon name='chevronLeft' />}
          >
            Back to presets
          </Button>

          <Box className='internal-colorpicker__gradient-container'>
            <Box
              ref={gradientRef}
              className='internal-colorpicker__gradient'
              onClick={handleGradientClick}
              style={{ backgroundColor: `hsl(${hue}, 100%, 50%)` }}
            >
              <Box
                className='internal-colorpicker__gradient-selector'
                style={gradientSelectorPosition}
              />
            </Box>
          </Box>

          <Box
            ref={hueSliderRef}
            className='internal-colorpicker__hue-slider'
            onClick={handleHueSliderClick}
          >
            <Box className='internal-colorpicker__hue-selector' style={hueSliderPosition} />
          </Box>

          <Box className='internal-colorpicker__controls'>
            <Dropdown
              options={[
                { value: 'HEX', label: 'HEX' },
                { value: 'RGB', label: 'RGB' },
              ]}
              value={format}
              onChange={(value) => setFormat(value as ColorFormat)}
              fullWidth={false}
              variant='default'
            />

            {format === 'HEX' ? (
              <ColorInput
                name='color'
                value={getFormattedColor()}
                onChange={handleColorInput}
                disablePicker
                className='internal-colorpicker__color-input'
              />
            ) : (
              <Box className='internal-colorpicker__rgb-inputs'>
                <TextField
                  value={hexToRgb(currentColor).r}
                  onChange={(e) => handleRgbChange('r', parseInt(e.target.value) || 0)}
                  size='small'
                  className='internal-colorpicker__rgb-input'
                />
                <TextField
                  value={hexToRgb(currentColor).g}
                  onChange={(e) => handleRgbChange('g', parseInt(e.target.value) || 0)}
                  size='small'
                  className='internal-colorpicker__rgb-input'
                />
                <TextField
                  value={hexToRgb(currentColor).b}
                  onChange={(e) => handleRgbChange('b', parseInt(e.target.value) || 0)}
                  size='small'
                  className='internal-colorpicker__rgb-input'
                />
              </Box>
            )}
          </Box>
        </Box>
      )}
    </Box>
  )
}

export default ColorPicker
